import { CreatePersonalAccessTokenDialogCtrl, PersonalAccessToken } from '../controllers/CreatePersonalAccessTokenDialogCtrl';
import { FeatureFlags } from '../FeatureFlags';
import { ActionContext } from '../helpers/ActionContext';
import { CommonErrorCode } from '../helpers/ErrorCodes';
import { IServerUserInfo } from '../models/IServerUserInfo';
import { Module } from '../Module';
import { ServerUsers } from '../resources/ServerUsers';
import { CreatePersonalAccessTokenRequest, CreatePersonalAccessTokenResult } from '../ServerApi/requests/CreatePersonalAccessTokenRequest';
import { IConnectedDevice } from '../ServerApi/types/IConnectedDevice';
import { IPersonalAccessToken } from '../ServerApi/types/IPersonalAccessToken';
import { DeletePersonalAccessTokenAction } from './DeletePersonalAccessTokenAction';
import { ModalService, ModalSize } from './ModalService';
import { ServerService } from './ServerService';
import { ToasterService, ToastType } from './ToasterService';

export interface PersonalAccessTokensScope extends ng.IScope {
    // Create New Token
    controls: {personalAccessTokenName: string};
    createNewPersonalAccessToken: () => void;
    isPersonalAccessTokenNameEmpty: () => boolean;
    isPersonalAccessTokenNameUsed: () => boolean;
    isCreateNewPersonalAccessTokenDisabled: () => boolean;
    canCreatePersonalAccessTokens: boolean;

    // List All Tokens
    connectedPersonalAccessTokens: IPersonalAccessToken[];
    deletePersonalAccessToken(token: IPersonalAccessToken, $event: JQueryEventObject): void;

    connectedDevices: IConnectedDevice[];
}

export class PersonalAccessTokensService {

  public static DEVICE_NAME_FOR_TOKENS = 'PERSONAL_ACCESS_TOKEN';
  private serverService: ServerService;
  private serverUsers: ServerUsers;
  private modalService: ModalService;
  private $translate: ng.translate.ITranslateService;
  private deletePersonalAccessTokenAction: DeletePersonalAccessTokenAction;
  private toaster: ToasterService;

  public static $inject = [
    'ServerService',
    'ServerUsers',
    'ModalService',
    '$translate',
    'DeletePersonalAccessTokenAction',
    'ToasterService',
  ];

  constructor(
      serverService: ServerService,
      serverUsers: ServerUsers,
      modalService: ModalService,
      $translate: ng.translate.ITranslateService,
      deletePersonalAccessTokenAction: DeletePersonalAccessTokenAction,
      toaster: ToasterService,
      ) {
    this.serverService = serverService;
    this.serverUsers = serverUsers;
    this.modalService = modalService;
    this.$translate = $translate;
    this.deletePersonalAccessTokenAction = deletePersonalAccessTokenAction;
    this.toaster = toaster;
  }

  public loadTokensIntoScope(scope: PersonalAccessTokensScope, serverUser: IServerUserInfo): () => void {
    // If the feature is disabled, we leave the scope as is.
    if (!FeatureFlags.personalAccessTokensEnabled) {
        return () => {}; // Empty callback
     }

    const refreshTokensAndDevices = () => {
      // Refresh Personal Access Tokens
      this.serverUsers.getPersonalAccessTokens(serverUser.id)
        .then((tokens: IPersonalAccessToken[]) => scope.connectedPersonalAccessTokens = tokens);

      if (scope.connectedDevices) {
        // Removing the Personal Access Tokens device from the Connected Clients list.
        scope.connectedDevices = scope.connectedDevices.filter(x => x.id !== PersonalAccessTokensService.DEVICE_NAME_FOR_TOKENS);
      }
    };
    return refreshTokensAndDevices;
  }

  public loadActionsIntoScope(scope: PersonalAccessTokensScope, serverUser: IServerUserInfo, isSessionUserSelf: boolean,
    refreshDevicesAndTokens: () => void) {
    if (!FeatureFlags.personalAccessTokensEnabled) {
        return;
    }

    // Create tokens should only appear on the personal settings page, since we are always creating tokens for oneself.
    scope.canCreatePersonalAccessTokens = isSessionUserSelf;
    scope.createNewPersonalAccessToken = () => this.createNewPersonalAccessToken(scope, refreshDevicesAndTokens);

    scope.isPersonalAccessTokenNameEmpty = () => {
      const name = scope.controls.personalAccessTokenName;
      return !name || name.length === 0;
    };

    scope.isPersonalAccessTokenNameUsed = () => {
      const name = scope.controls.personalAccessTokenName;
      const tokens = scope.connectedPersonalAccessTokens;
      return tokens && tokens.some(x => x.clientId === name);
    };

    scope.isCreateNewPersonalAccessTokenDisabled = () => {
      return scope.isPersonalAccessTokenNameEmpty() || scope.isPersonalAccessTokenNameUsed();
    };

    scope.deletePersonalAccessToken = (token: IPersonalAccessToken, $event: JQueryEventObject) =>
      this.deletePersonalAccessTokenAction.execute(token, serverUser.id, ActionContext.fromEvent($event))
        .then(refreshDevicesAndTokens);
  }

  private createNewPersonalAccessToken = (scope: PersonalAccessTokensScope, refreshDevicesAndTokens: () => void) => {
    this.serverService.sendRequest(new CreatePersonalAccessTokenRequest({
        clientId: scope.controls.personalAccessTokenName
    })).then((result: CreatePersonalAccessTokenResult) => {
      if (result && result.refreshToken) {
        this.openNewTokenDialog(scope, result.refreshToken, refreshDevicesAndTokens);
      } else {
        this.processCreateTokenError(result);
      }
    });
  }

  private processCreateTokenError(result: CreatePersonalAccessTokenResult) {
    let errorMessageKey = 'errorUnknown';
    if (result && result.errors && result.errors.length) {
      switch (result.errors[0].code) {
        case CommonErrorCode.MaximumRefreshTokenReached:
          errorMessageKey = 'personalAccessTokensMaximumLimitReached';
      }
    }
    this.toaster.pop({ type: ToastType.Error, text: this.$translate.instant(errorMessageKey) });
  }

  private openNewTokenDialog(scope: PersonalAccessTokensScope, refreshToken: string, refreshDevicesAndTokens: () => void) {
    const token: PersonalAccessToken = {
      tokenName: scope.controls.personalAccessTokenName,
      tokenSecret: refreshToken,
    };
    const dialogInstance = this.modalService.open({
      scope: scope.$parent,
      templateUrl: 'createPersonalAccessTokenDialog.html',
      size: ModalSize.fitToContent,
      controller: CreatePersonalAccessTokenDialogCtrl,
      resolve: {
        token: () => token,
      },
    });
    dialogInstance.result.then(() => {
      scope.controls.personalAccessTokenName = '';
    });
    dialogInstance.result.finally(refreshDevicesAndTokens);
  }
}

Module.instance().service('PersonalAccessTokensService', PersonalAccessTokensService);
